import React, { useEffect, useState, useRef } from "react";
import { generateClassName, ObjTest } from "jzfz-web-utils";
import { Button, message, Modal, Spin, Table, Input, Form } from "antd";
import {
    LeftOutlined,
    RightOutlined,
    VerticalAlignBottomOutlined,
    FullscreenOutlined,
    EditFilled,
} from '@ant-design/icons';
import { fileDownload, request } from "../../util/request";
import { GLOBAL_OCR_API } from "../../config/api";
import moment from "moment";
import { openAttachmentPreviewer } from "./utils";
import { downloadAttachment, getAttachmentPreviewURL } from '../../api/commonAPI';
import './style/style.less';
import Draggable from "react-draggable";
import { ModuleEnum } from "../../config/constant";
import {AttachmentDataSource} from "./interface";


// 可以预览的文件类型
const previewFileTypes = ['txt', 'pdf', 'jpg', 'jpeg', 'png', 'gif'];
const { TextArea } = Input;
const textParsingType = ['mp4', 'mp3'] // 文字解析支持格式


interface AttachmentProps {
    className?: string;
    contentClassName?: string;
    dataSource: AttachmentDataSource[];
    defaultActiveIndex?: number;    // 默认展示的附件的下标
    downloadable?: boolean; // 允许下载附件 默认true
    isEditVideoText?: boolean;
    appType?: ModuleEnum; // 知识类型
}


/**
 * 预览
 * @param url
 */
export const filePreview = (name: string, url: string) => {
    if (!url) {
        message.error('无效下载链接');
        return;
    }
    // 新开页面预览
    window.open(url, '_blank');
}


/**
 * 公用附件组件
 * @param props
 * @constructor
 */
const Attachment = (props: AttachmentProps) => {
    const {
        className,
        contentClassName,
        dataSource = [],
        defaultActiveIndex,
        downloadable: shouldDownload = true,
        isEditVideoText = false,
        appType = undefined,
    } = props;
    const [loading, setLoading] = useState<boolean>(false);
    const [resultLoading, setResultLoading] = useState<boolean>(false);
    const [previewInfo, setPreviewInfo] = useState<any>({});  // 预览信息
    const [selectedPkid, setSelectedPkid] = useState<string>('');   // 选中的附件id
    const [resultContent, setResultContent] = useState<string>('');   // 视频文件识别结果
    const [currentAttachmentId, setCurrentAttachmentId] = useState<string>('');
    const [currentKnowledgePkId, setCurrentKnowledgePkId] = useState<string>('');
    const [videoModalVisible, setVideoModalVisible] = useState<boolean>(false);
    const [canEdit, setCanEdit] = useState<boolean>(false); // 可否编辑视频文本
    // 主体表单
    const [form] = Form.useForm();
    const { setFieldsValue, getFieldsValue } = form;
    const videoTextFocus = useRef<any>(null);
    const [editIconShow, setEditIconShow] = useState<boolean>(true);
    const [cancellFlag, setcancellFlag] = useState<boolean>(false);

    useEffect(() => {
        if (ObjTest.isNotEmptyArray(dataSource)) {
            let file = null;
            if (defaultActiveIndex !== undefined && defaultActiveIndex !== null) {
                file = dataSource[Number(defaultActiveIndex)];
            } else {
                file = dataSource.find((v: any) => v.previewable);  // 可预览的附件
            }
            if (file) {
                previewHandle(file, 1);
            }
        }
        return () => {
            setPreviewInfo({});
            setSelectedPkid('');
        }
    }, [dataSource, defaultActiveIndex])


    useEffect(() => {
        if (canEdit && videoModalVisible) {
            videoTextFocus.current.focus({ cursor: 'end' });
        }
    }, [canEdit, videoTextFocus, videoModalVisible])


    /**
     * 显示全部下载按钮
     */
    const showDownloadAll = (): boolean => {
        if (ObjTest.isNotEmptyArray(dataSource) && dataSource.some((v: any) => v.downloadable) && shouldDownload) {
            return true;
        }
        return false;
    }


    /**
     * 批量下载
     */
    const downloadAll = () => {
        let ids = dataSource.filter((v: any) => v.downloadable).map((v: any) => v.attachmentId);
        if (!ObjTest.isNotEmptyArray(ids)) {
            message.warning('没有可下载的附件');
            return;
        }
        ids.forEach((v: any) => {
            downloadAttachment(v, { appType });
        })
    }


    /**
     * 查询视屏文字提取结果
     * @param attachmentId 附件id
     */
    const queryVideoToWordResult = (knowledgePkId: string, attachmentId: string) => {
        setResultLoading(true);
        let param = {
            attachmentIdList: [attachmentId],
            knowledgePkId,
        }
        request('/knowledge-video-word/results', param, { baseURL: GLOBAL_OCR_API }).then((res: any) => {
            const [first] = res.data;
            setResultContent((first || {}).urlContent || '');
            setCurrentAttachmentId((first || {}).attachmentId || '');
            setCurrentKnowledgePkId((first || {}).knowledgePkId || '');
        }).finally(() => {
            setResultLoading(false);
        })
    };


    /**
     * 预览
     * @param data
     * @param type 1:预览 2:全屏
     */
    const previewHandle = (data: any, type: number) => {
        const { fileName, attachmentId, previewable, uploadKnowledgeId } = data;

        if (!previewable) {
            return;
        }

        // 选中附件
        setSelectedPkid(attachmentId);
        setResultContent(null);// 清空视频文本识别
        let fileNameType = fileName.split('.').slice(-1)[0];
        const isViewResultForVideo = textParsingType.includes(fileNameType);
        if (isViewResultForVideo) {
            queryVideoToWordResult(uploadKnowledgeId, attachmentId);
        }
        setLoading(true);
        getAttachmentPreviewURL(attachmentId).then((res: any) => {
            if (type === 1) {
                setPreviewInfo({ fileName, src: res.data });
            }
            if (type === 2) {
                openAttachmentPreviewer({ url: res.data, fileName });
            }
        }).finally(() => {
            setLoading(false);
        })
    }


    /**
     * 下载
     * @param data
     */
    const downloadHandle = (data: any) => {
        const { attachmentId } = data;
        setLoading(true);
        downloadAttachment(attachmentId, { appType }).then((res: any) => {
            message.success('下载成功');
        }).finally(() => {
            setLoading(false);
        })
    }


    /**
     * 获取按钮禁用状态
     * @param direction 1: 上一条  2: 下一条
     */
    const getBtnDisabled = (direction: 1 | 2): boolean => {
        let previewableFiles = dataSource.filter((v: any) => v.previewable);    // 可预览附件集合
        let index = previewableFiles.findIndex((v: any) => v.attachmentId === selectedPkid);
        if (direction === 1 && index === 0) {
            return true;
        }
        if (direction === 2 && index === previewableFiles.length - 1) {
            return true;
        }
        return false;
    }


    /**
     * 左右切换
     * @param direction 1: 上一条  2: 下一条
     */
    const pageChange = (direction: 1 | 2) => {
        let previewableFiles = dataSource.filter((v: any) => v.previewable);    // 可预览附件集合
        let index = previewableFiles.findIndex((v: any) => v.attachmentId === selectedPkid);
        if (index === -1) {
            return;
        }
        if (direction === 1) {
            index--;
            if (index < 0) {
                return;
            }
        }
        if (direction === 2) {
            index++;
            if (index > previewableFiles.length - 1) {
                return;
            }
        }
        let file = previewableFiles[index];
        previewHandle(file, 1);
    }


    /**
     * 修改视频文字内容
     * @param urlContent
     */
    const editVideoText = (urlContent: string) => {
        let param = {
            attachmentId: currentAttachmentId,
            urlContent,
        }
        setResultLoading(true);
        request('/knowledge-video-word/update-result', param, { baseURL: GLOBAL_OCR_API }).then((res: any) => {
            message.success('文本修改成功');
            setResultContent(urlContent);
        }).finally(() => {
            setResultLoading(false);
        })
    }


    /**
     * 编辑视频文字保存按钮
     */
    const saveVideoText = () => {
        const { videoText: urlContent } = getFieldsValue();
        editVideoText(urlContent);
        setVideoModalVisible(false);
    }


    const getBtns = (record: any): React.ReactNode[] => {
        const { previewable, downloadable } = record;
        let btns = [];
        if (previewable) {
            btns = [
                ...btns,
                <Button className="f12" type="link" size="small" onClick={() => previewHandle(record, 1)}>预览</Button>,
                <Button className="f12" type="link" size="small" onClick={() => previewHandle(record, 2)}>全屏查看</Button>,
            ];
        }
        if (downloadable && shouldDownload) {
            btns = [
                ...btns,
                <Button className="f12" type="link" size="small" onClick={() => downloadHandle(record)}>下载</Button>,
            ];
        }
        return btns;
    }


    const columns: any[] = [
        {
            className: 'drag-visible',
            title: (<div className="tc">文件名</div>),
            dataIndex: 'fileName',
            render: (text: string, record: any, index: number) => {
                return {
                    children: (<div className="pl3 eps" title={text} onClick={() => previewHandle(record, 1)}>{text}</div>),
                    props: {
                        className: selectedPkid === record.attachmentId ? 'active' : '',
                        rowSpan: record.idRowSpan,
                    }
                }
            }
        },
        {
            title: '大小',
            dataIndex: 'size',
            align: 'center',
            width: 90,
            ellipsis: true,
        },
        {
            title: '上传日期',
            dataIndex: 'uploadTime',
            width: 90,
            align: 'center',
            ellipsis: true,
            render: (text: string, record: any, index: number) => {
                return text ? moment(text).format('YYYY-MM-DD') : text;
            }
        },
        {
            title: (
                <div className="ml8 flexic">
                    <span>操作</span>
                    {
                        showDownloadAll() && (
                            <Button
                                className="mla link-btn f12"
                                type="link"
                                size="small"
                                onClick={() => downloadAll()}
                            >全部下载</Button>
                        )
                    }
                </div>
            ),
            width: 160,
            render: (text: string, record: any, index: number) => {
                return (
                    getBtns(record).map((v: any, i: number) => (
                        <React.Fragment key={i}>
                            {i > 0 && <span className="split-line">|</span>}
                            {v}
                        </React.Fragment>
                    ))
                )
            }
        },
    ]


    /**
     * 视频转文字弹窗取消事件
     */
    const cancellEvents = () => {
        if (cancellFlag) {
            setCanEdit(false);
            setEditIconShow(true);
            setResultContent(resultContent);
            return;
        }
        setVideoModalVisible(false);
        setFieldsValue({ videoText: resultContent });
    }


    /**
     * 视频独立预览弹窗
     */
    const videoModalRender = () => {
        return (
            <Modal
                footer={
                    canEdit ?
                        (
                            <>
                                <button onClick={cancellEvents} type="button" className="ant-btn">
                                    <span>取 消</span>
                                </button>
                                <button onClick={saveVideoText} type="button" className="ant-btn ant-btn-primary">
                                    <span>保 存</span>
                                </button>
                            </>
                        ) :
                        null
                }
                visible={videoModalVisible}
                className="video-modal-container"
                width="500px"
                maskClosable={false}
                mask={false}
                okText='保存'
                onOk={saveVideoText}
                onCancel={() => setVideoModalVisible(false)}
                wrapClassName='video-modal-container-wrap'
                modalRender={modal => (
                    <Draggable
                        handle=".video-title"
                    >
                        {modal}
                    </Draggable>
                )}
            >
                <Form form={form} name='videoTextEditForm'>
                    <div className="video-title" style={{ cursor: 'move' }}>视频文字</div>
                    <div className={generateClassName(['video-text-container', 'ofa', ' h', canEdit ? '' : 'rounded-corner'])} style={{ background: '#555555', color: '#FFFFFF' }}>
                        {
                            canEdit ?
                                (
                                    <Form.Item name='videoText' initialValue={resultContent} noStyle>
                                        <TextArea
                                            ref={videoTextFocus}
                                            className="video-text-section"
                                            autoFocus
                                        />
                                    </Form.Item>
                                ) :
                                (
                                    <div className="video-text-section">{resultContent}</div>
                                )
                        }
                    </div>
                </Form>
                {
                    !canEdit && isEditVideoText && (
                        <div
                            className='editPattern'
                            onClick={() => {
                                setCanEdit(true);
                                setEditIconShow(false);
                                setFieldsValue({ videoText: resultContent });
                            }}
                        >
                            {
                                editIconShow &&
                                (
                                    <EditFilled style={{ color: '#fff', fontSize: '16px' }}
                                    />
                                )
                            }
                        </div>
                    )
                }
            </Modal>
        )
    }

    return (
        <div className={generateClassName(['attachment-previewer-wrap', className])}>
            {
                (previewInfo || {}).src && (
                    <div className="preview-box">
                        <div className="header eps" title={previewInfo.fileName}>{previewInfo.fileName}</div>
                        <div className="preview-box-main">
                            <iframe className="w h" src={previewInfo.src} style={{ border: 0 }} allowFullScreen></iframe>
                            <div
                                className={generateClassName(['preview-prev-btn', getBtnDisabled(1) ? 'preview-disabled-btn' : ''])}
                                onClick={() => pageChange(1)}
                            >
                                <LeftOutlined />
                            </div>
                            <div
                                className={generateClassName(['preview-next-btn', getBtnDisabled(2) ? 'preview-disabled-btn' : ''])}
                                onClick={() => pageChange(2)}
                            >
                                <RightOutlined />
                            </div>
                        </div>
                        {
                            resultContent && (
                                <>
                                    <div className="video-speech flexic">
                                        <img className="mlr36 text-parsing-img" src={require('../../assets/images/text-parsing.png')} />
                                        <div className="flex1 ofa"
                                            style={{ height: 110 }}
                                        >
                                            {resultContent}
                                        </div>
                                        <div className="extension-icon flexc edit-icon">
                                            {
                                                isEditVideoText && (
                                                    <EditFilled
                                                        style={{ color: '#fff', fontSize: '16px' }}
                                                        className="mr15 crp mb24 mr4 enlarge-icon"
                                                        onClick={() => {
                                                            setVideoModalVisible(true);
                                                            queryVideoToWordResult(currentKnowledgePkId, currentAttachmentId);
                                                            setCanEdit(true);
                                                            setcancellFlag(false);
                                                        }}
                                                    />
                                                )
                                            }
                                            <FullscreenOutlined
                                                style={{ color: '#fff', fontSize: '16px', paddingRight: '14px' }}
                                                className="enlarge-icon crp"
                                                onClick={() => {
                                                    setVideoModalVisible(true);
                                                    queryVideoToWordResult(currentKnowledgePkId, currentAttachmentId);
                                                    setCanEdit(false);
                                                    setEditIconShow(true);
                                                    setcancellFlag(true);
                                                }}
                                            />
                                        </div>
                                    </div>
                                    {
                                        videoModalVisible && videoModalRender()
                                    }
                                    <Spin className="custom-fixed-loading-wrap" spinning={loading} size="large" />
                                </>
                            )
                        }
                    </div>
                )
            }
            <Table
                className="attachment-table"
                rowKey="attachmentId"
                bordered
                loading={loading}
                dataSource={dataSource}
                columns={columns}
                sticky={true}
                pagination={false}
            />
        </div>
    )
}


export default Attachment;
